import { getOpacityForGeoEventMarker } from 'features/mapping/icons';
import { LngLat, LngLatBounds } from 'shared/map-exports';
import { WildfireListItem } from 'features/wildfires/wildfire.types';
import { useMapState, UseMapState } from 'state/useMapState';
import { useAllGeoEvents } from 'features/geoEvents/hooks/useAllGeoEvents';
import { FloodingListItem } from 'features/flooding/flooding.types';
import { SerializedBounds } from 'shared/types';
import { useMemo } from 'react';

type UseVisibleGeoEventsProps = {
  enabled?: boolean;
};

export const getMapBounds = (
  bounds: SerializedBounds,
): mapboxgl.LngLatBounds => {
  const southWest = new LngLat(
    // eslint-disable-next-line no-underscore-dangle
    (bounds._southWest?.lng || bounds._sw?.lng)!,
    // eslint-disable-next-line no-underscore-dangle
    (bounds._southWest?.lat || bounds._sw?.lat)!,
  );
  const northEast = new LngLat(
    // eslint-disable-next-line no-underscore-dangle
    (bounds._northEast?.lng || bounds._ne?.lng)!,
    // eslint-disable-next-line no-underscore-dangle
    (bounds._northEast?.lat || bounds._ne?.lat)!,
  );

  return new LngLatBounds(southWest, northEast);
};

const getGeoEventsInViewport = (
  bounds: UseMapState['incidentMapState']['bounds'],
  geoEvents: (WildfireListItem | FloodingListItem)[],
): (WildfireListItem | FloodingListItem)[] => {
  if (!bounds) return [];

  const bbounds = getMapBounds(bounds);

  return geoEvents
    .filter(
      (geoEvent) =>
        bbounds.contains(new LngLat(geoEvent.lng, geoEvent.lat)) &&
        getOpacityForGeoEventMarker({ geoEvent, isFadable: true }) > 0,
    )
    .sort((a, b) => {
      return (
        new Date(b.dateModified).getTime() - new Date(a.dateModified).getTime()
      );
    });
};

export const useVisibleGeoEvents = (
  props: UseVisibleGeoEventsProps,
): (WildfireListItem | FloodingListItem)[] => {
  const { enabled = true } = props;
  const { incidentMapState } = useMapState();
  const { bounds } = incidentMapState;

  const { wildfireEvents, floodingEvents } = useAllGeoEvents({ enabled });

  return useMemo(
    () =>
      getGeoEventsInViewport(bounds, [...wildfireEvents, ...floodingEvents]),
    [bounds, wildfireEvents, floodingEvents],
  );
};
